import React from "react";
import ContentPage from "../components/ContentPage";

export default function ExamplesPage() {
  return (
    <ContentPage title="See Top Screen&#8482; in Action">
      <div className="prose prose-invert max-w-none" data-testid="examples-content">
        <p className="text-[#E8E4DC] text-lg mb-8">
          Discover how professionals, students, and everyday users leverage Top Screen&#8482; to transform their workflow.
        </p>

        <div className="space-y-8">
          {/* Use Case 1 */}
          <div className="card-glass p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Video Calls While Working</h3>
                <span className="text-blue-400 text-xs font-semibold uppercase">Remote Workers</span>
              </div>
            </div>
            <p className="text-[#E8E4DC] mb-4">Keep your Zoom, Teams, or Google Meet call visible in a corner while you work on documents, code, or spreadsheets. Never miss a facial expression or shared screen again.</p>
            <div className="bg-black/40 rounded-lg p-4">
              <p className="text-[#E8E4DC] text-xs mb-2">Example setup:</p>
              <ul className="text-[#E8E4DC] text-sm space-y-1">
                <li>&#8226; Frame: <span className="text-white">Classic</span> with blue border</li>
                <li>&#8226; Size: <span className="text-white">Medium (480&#215;360)</span></li>
                <li>&#8226; Position: <span className="text-white">Bottom-right</span></li>
                <li>&#8226; Timer: <span className="text-white">Auto-show 5 min before scheduled meetings</span></li>
              </ul>
            </div>
          </div>

          {/* Use Case 2 */}
          <div className="card-glass p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/15 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Live Stock Monitoring</h3>
                <span className="text-green-400 text-xs font-semibold uppercase">Day Traders</span>
              </div>
            </div>
            <p className="text-[#E8E4DC] mb-4">Pin your TradingView chart, stock screener, or portfolio dashboard as an always-on-top overlay while executing trades on your brokerage platform. Set alarms for market open/close.</p>
            <div className="bg-black/40 rounded-lg p-4">
              <p className="text-[#E8E4DC] text-xs mb-2">Example setup:</p>
              <ul className="text-[#E8E4DC] text-sm space-y-1">
                <li>&#8226; Frame: <span className="text-white">Neon Glow</span> with green accent</li>
                <li>&#8226; Size: <span className="text-white">Large (640&#215;480)</span></li>
                <li>&#8226; Multiple instances: <span className="text-white">3 charts + 1 news feed</span></li>
                <li>&#8226; Alarm: <span className="text-white">9:25 AM — 5 min before market open</span></li>
              </ul>
            </div>
          </div>

          {/* Use Case 3 */}
          <div className="card-glass p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/15 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Tutorial Reference</h3>
                <span className="text-purple-400 text-xs font-semibold uppercase">Students &amp; Developers</span>
              </div>
            </div>
            <p className="text-[#E8E4DC] mb-4">Pin a YouTube tutorial, Udemy course, or documentation page as an overlay while you code, design, or study. Follow along step-by-step without ever losing the video.</p>
            <div className="bg-black/40 rounded-lg p-4">
              <p className="text-[#E8E4DC] text-xs mb-2">Example setup:</p>
              <ul className="text-[#E8E4DC] text-sm space-y-1">
                <li>&#8226; Frame: <span className="text-white">Shadow</span> for depth</li>
                <li>&#8226; Size: <span className="text-white">Small (320&#215;240)</span></li>
                <li>&#8226; Opacity: <span className="text-white">85%</span> — slightly transparent to see behind</li>
                <li>&#8226; Audio Override: <span className="text-white">On</span> — tutorial audio stays independent</li>
              </ul>
            </div>
          </div>

          {/* Use Case 4 */}
          <div className="card-glass p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Live Streaming &amp; Content Creation</h3>
                <span className="text-red-400 text-xs font-semibold uppercase">Streamers</span>
              </div>
            </div>
            <p className="text-[#E8E4DC] mb-4">Monitor your Twitch/YouTube chat, donation feed, and viewer count while gaming or creating content. The RGB Backlight frame adds a professional touch to stream recordings.</p>
            <div className="bg-black/40 rounded-lg p-4">
              <p className="text-[#E8E4DC] text-xs mb-2">Example setup:</p>
              <ul className="text-[#E8E4DC] text-sm space-y-1">
                <li>&#8226; Frame: <span className="text-white">RGB Backlight</span> — animated rainbow border</li>
                <li>&#8226; Theatre Mode: <span className="text-white">On</span> during gameplay</li>
                <li>&#8226; Multiple instances: <span className="text-white">Chat + Donations + Alerts</span></li>
              </ul>
            </div>
          </div>

          {/* Use Case 5 */}
          <div className="card-glass p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/15 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Team War Room / NOC</h3>
                <span className="text-yellow-400 text-xs font-semibold uppercase">IT Operations &amp; Trading Floors</span>
              </div>
            </div>
            <p className="text-[#E8E4DC] mb-4">Share critical dashboards, server status boards, and alert feeds across your team's screens. Everyone sees the same overlays. No expensive hardware required — just Top Screen&#8482; Team Collaboration.</p>
            <div className="bg-black/40 rounded-lg p-4">
              <p className="text-[#E8E4DC] text-xs mb-2">Example setup:</p>
              <ul className="text-[#E8E4DC] text-sm space-y-1">
                <li>&#8226; 10 team members, each with <span className="text-white">synced overlays</span></li>
                <li>&#8226; Alarm: <span className="text-white">Instant pop-up on critical alerts</span></li>
                <li>&#8226; Frame: <span className="text-white">Classic with red border</span> for priority</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ContentPage>
  );
}
